import { combineReducers } from '@reduxjs/toolkit';

import { accountSettingsReducer } from '@/features/account/account-settings/store/account-settings.slice';
import { emailChangeReducer } from '@/features/account/email-change/store/email-change.slice';
import { notificationPreferencesReducer } from '@/features/account/notification-preferences/store/notification-preferences.slice';
import { passwordChangeReducer } from '@/features/account/password-change/store/password-change.slice';
import { securitySessionsReducer } from '@/features/account/security-sessions/store/security-sessions.slice';
import { addAdministratorReducer } from '@/features/administrator/add-administrator/store/add-administrator.slice';
import { administratorsReducer } from '@/features/administrator/administrators/store/administrators.slice';
import { editAdministratorReducer } from '@/features/administrator/edit-administrator/store/edit-administrator.slice';
import { apiKeysReducer } from '@/features/api-key/api-keys/store/api-keys.slice';
import { editApiKeyReducer } from '@/features/api-key/edit-api-key/store/edit-api-key.slice';
import { confirmEmailChangeReducer } from '@/features/auth/confirm-email-change/store/confirm-email-change.slice';
import { forgotPasswordReducer } from '@/features/auth/forgot-password/store/forgot-password.slice';
import { loginReducer } from '@/features/auth/login/store/login.slice';
import { onboardingReducer } from '@/features/auth/onboarding/store/onboarding.slice';
import { resetPasswordReducer } from '@/features/auth/reset-password/store/reset-password.slice';
import { profileTypesReducer } from '@/features/classification/profile-types/store/profile-types.slice';
import { companiesReducer } from '@/features/company/companies/store/companies.slice';
import { companyDetailReducer } from '@/features/company/company-detail/store/company-detail.slice';
import { institutionalDetailReducer } from '@/features/company/institutional-detail/store/institutional-detail.slice';
import { institutionalsReducer } from '@/features/company/institutionals/store/institutionals.slice';
import { dashboardReducer } from '@/features/dashboard/dashboard/store/dashboard.slice';
import { metricsReducer } from '@/features/enrichment/metrics/store/metrics.slice';
import { sessionDetailReducer } from '@/features/enrichment/session-detail/store/session-detail.slice';
import { sessionsReducer } from '@/features/enrichment/sessions/store/sessions.slice';
import { adminFaqReducer } from '@/features/faq/admin-faq/store/admin-faq.slice';
import { clientFaqReducer } from '@/features/faq/client-faq/store/client-faq.slice';
import { addFundraisingReducer } from '@/features/fundraising/add-fundraising/store/add-fundraising.slice';
import { editFundraisingReducer } from '@/features/fundraising/edit-fundraising/store/edit-fundraising.slice';
import { fundraisingsReducer } from '@/features/fundraising/fundraisings/store/fundraisings.slice';
import { addLicenceReducer } from '@/features/licence/add-licence/store/add-licence.slice';
import { licenceDetailReducer } from '@/features/licence/licence-detail/store/licence-detail.slice';
import { licencesReducer } from '@/features/licence/licences/store/licences.slice';
import { myLicenceReducer } from '@/features/licence/my-licence/store/my-licence.slice';
import { addListReducer } from '@/features/list/add-list/store/add-list.slice';
import { clientImportAlertsReducer } from '@/features/list/client-import-alerts/store/client-import-alerts.slice';
import { discoveredLpDetailReducer } from '@/features/list/discovered-lp-detail/store/discovered-lp-detail.slice';
import { discoveredLpsReducer } from '@/features/list/discovered-lps/store/discovered-lps.slice';
import { editListReducer } from '@/features/list/edit-list/store/edit-list.slice';
import { fileImportReducer } from '@/features/list/file-import/store/file-import.slice';
import { importsReducer } from '@/features/list/imports/store/imports.slice';
import { listDetailReducer } from '@/features/list/list-detail/store/list-detail.slice';
import { listFeedbacksReducer } from '@/features/list/list-feedbacks/store/list-feedbacks.slice';
import { listsReducer } from '@/features/list/lists/store/lists.slice';
import { deactivatedProfilesReducer } from '@/features/profile/deactivated-profiles/store/deactivated-profiles.slice';
import { editProfileIdentityReducer } from '@/features/profile/edit-profile-identity/store/edit-profile-identity.slice';
import { exitsInvestmentsReviewReducer } from '@/features/profile/exits-investments-review/store/exits-investments-review.slice';
import { myProfilesReducer } from '@/features/profile/my-profiles/store/my-profiles.slice';
import { nameRulesReducer } from '@/features/profile/name-rules/store/name-rules.slice';
import { profileCleaningReducer } from '@/features/profile/profile-cleaning/store/profile-cleaning.slice';
import { profileDetailReducer } from '@/features/profile/profile-detail/store/profile-detail.slice';
import { profileExportReducer } from '@/features/profile/profile-export/store/profile-export.slice';
import { profilesReducer } from '@/features/profile/profiles/store/profiles.slice';
import { trackedLpsReducer } from '@/features/profile/tracked-lps/store/tracked-lps.slice';
import { mySchoolsReducer } from '@/features/school/my-schools/store/my-schools.slice';
import { schoolsReducer } from '@/features/school/schools/store/schools.slice';
import { addUserReducer } from '@/features/user/add-user/store/add-user.slice';
import { editUserReducer } from '@/features/user/edit-user/store/edit-user.slice';
import { usersReducer } from '@/features/user/users/store/users.slice';

/**
 * Application root reducer.
 *
 * Grows one key per migrated feature. Each feature owns its slice under
 * `features/list/<slice>/store/`; this file is the only place that assembles them.
 */
export const rootReducer = combineReducers({
  accountSettings: accountSettingsReducer,
  dashboard: dashboardReducer,
  securitySessions: securitySessionsReducer,
  passwordChange: passwordChangeReducer,
  emailChange: emailChangeReducer,
  notificationPreferences: notificationPreferencesReducer,
  forgotPassword: forgotPasswordReducer,
  resetPassword: resetPasswordReducer,
  confirmEmailChange: confirmEmailChangeReducer,
  onboarding: onboardingReducer,
  addAdministrator: addAdministratorReducer,
  editAdministrator: editAdministratorReducer,
  administrators: administratorsReducer,
  apiKeys: apiKeysReducer,
  editApiKey: editApiKeyReducer,
  login: loginReducer,
  addList: addListReducer,
  discoveredLps: discoveredLpsReducer,
  discoveredLpDetail: discoveredLpDetailReducer,
  editList: editListReducer,
  fileImport: fileImportReducer,
  imports: importsReducer,
  addLicence: addLicenceReducer,
  licences: licencesReducer,
  licenceDetail: licenceDetailReducer,
  myLicence: myLicenceReducer,
  lists: listsReducer,
  listDetail: listDetailReducer,
  listFeedbacks: listFeedbacksReducer,
  clientImportAlerts: clientImportAlertsReducer,
  profiles: profilesReducer,
  myProfiles: myProfilesReducer,
  trackedLps: trackedLpsReducer,
  deactivatedProfiles: deactivatedProfilesReducer,
  profileDetail: profileDetailReducer,
  editProfileIdentity: editProfileIdentityReducer,
  profileExport: profileExportReducer,
  profileCleaning: profileCleaningReducer,
  exitsInvestmentsReview: exitsInvestmentsReviewReducer,
  profileTypes: profileTypesReducer,
  nameRules: nameRulesReducer,
  adminFaq: adminFaqReducer,
  clientFaq: clientFaqReducer,
  users: usersReducer,
  addUser: addUserReducer,
  editUser: editUserReducer,
  companies: companiesReducer,
  schools: schoolsReducer,
  mySchools: mySchoolsReducer,
  companyDetail: companyDetailReducer,
  institutionals: institutionalsReducer,
  institutionalDetail: institutionalDetailReducer,
  sessions: sessionsReducer,
  sessionDetail: sessionDetailReducer,
  metrics: metricsReducer,
  fundraisings: fundraisingsReducer,
  addFundraising: addFundraisingReducer,
  editFundraising: editFundraisingReducer,
});

export type RootState = ReturnType<typeof rootReducer>;
